import React, { useState } from 'react'
import PropTypes from 'prop-types'
import ConditionLabel from 'components/feature/ConditionLabel/ConditionLabelContainer'
import Editor from 'components/feature/Editor'
import { ReadOnlyParameterValue } from 'components/feature/NavigatorPanes/editors/base'
import OutputtingDataTo from 'components/feature/NavigatorPanes/OutputtingDataTo'
import { ChecRow, ChecCol } from 'components/chec'
import { RadioButtonGroup, DrawerDivider } from 'components/patterns'
import { TextInput, TimerInput } from 'components/patterns/Inputs'
import { Header } from 'components/patterns/Typography'
import { COMPLEX, QUERY_OPERATORS } from 'utils/constants'
import strings from 'utils/strings'
import { timerValue } from 'utils/timers'
import { compose, isEmpty } from 'ramda'

import { TIMER_MASK_HMMSSS } from 'constants/ui/timers'

import querySchema from 'schemas/validation/feature/querySchema'
import { schemaProp } from 'schemas/validation'

import withStyle, {
  ListLabel,
  ConditionsContainer,
} from './LogicSetEditor.style.js'
import { ViewNotesButton } from '../NavigatorPanes/FeatureNotes/FeatureNotes.jsx'
import LogicSetInfoModal from '../LogicSetInfoModal'
import { LearnHowLink } from '../LogicSetInfoModal/LogicSetInfoModal.style'
import { Icon__Info as InfoIcon } from 'components/patterns/Icon/components'

const rule = schemaProp(querySchema)

const options = QUERY_OPERATORS.map((op) => ({
  value: op,
  text: strings.query_operator_label(op),
}))

const LogicSetEditor = ({
  className,
  query,
  uri,
  description,
  finalComplexQueryOperator,
  abComplexQueryOperator,
  cdComplexQueryOperator,
  updateFinalOperator,
  updateABOperator,
  updateCDOperator,
  updateDescription,
  updateFeatureDelayTimerActivate,
  updateFeatureDelayTimerDeactivate,
  delayActivate,
  delayDeactivate,
  toggleMarkForDeletion,
  deleteLogicSet,
  resetLogicSet,
  featureInDesign,
  allDescriptions,
  isEditing,
  isOrphan,
}) => {
  const [showInfoModal, setShowInfoModal] = useState(false)
  const logicSetEditable = isEditing && featureInDesign

  if (!query || isEmpty(query)) return ''

  const { conditions = [] } = query

  const operators = [
    {
      label: strings.logic_gate_top,
      onChange: updateABOperator,
      currentValue: abComplexQueryOperator,
    },
    {
      label: strings.logic_gate_middle,
      onChange: updateFinalOperator,
      currentValue: finalComplexQueryOperator,
    },
    {
      label: strings.logic_gate_bottom,
      onChange: updateCDOperator,
      currentValue: cdComplexQueryOperator,
    },
  ]

  const timers = [
    {
      value: delayActivate,
      onChange: updateFeatureDelayTimerActivate,
      name: 'Activate delay',
      label: strings.activate_delay,
    },
    {
      value: delayDeactivate,
      onChange: updateFeatureDelayTimerDeactivate,
      name: 'Deactivate delay',
      label: strings.deactivate_delay,
      className: 'input-timer-last',
    },
  ]

  return (
    <Editor
      className={className}
      toggleMarkForDeletion={toggleMarkForDeletion}
      markedForDeletion={query.markedForDeletion}
      deleteItem={deleteLogicSet}
      deleteHeaderText={strings.delete_item_heading(description)}
      deleteExplanation={strings.delete_logicset_explanation}
      clearItem={resetLogicSet}
      clearHeaderText={strings.clear_item_heading(query.description)}
      clearExplanation={strings.clear_logicset_explanation}
    >
      {!isOrphan && (
        <ViewNotesButton
          id={uri}
          fromType={COMPLEX}
          description={description}
        />
      )}
      <Header type="h3" className="form-title">
        {description}
      </Header>
      {logicSetEditable ? (
        <div className="logic-set-name-input">
          <TextInput
            required
            rules={rule('description', {
              descriptions: allDescriptions,
            })}
            label={strings.logic_set_name}
            name={strings.description}
            value={description}
            onChange={updateDescription}
          />
        </div>
      ) : (
        <DrawerDivider />
      )}

      <ConditionsContainer className="form-field">
        <ListLabel>{strings.queries}</ListLabel>
        {conditions.map((condition, index) => (
          <ConditionLabel
            className={`editor-section ${index > 0 ? 'condition-item' : ''}`}
            key={condition}
            uri={condition}
            ignoreCarry={true}
            data-testid={`condition-label-${index}`}
            isLink
          />
        ))}
      </ConditionsContainer>

      {logicSetEditable ? (
        <>
          {operators.map((operator) => (
            <RadioButtonGroup
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...operator}
              key={operator.label}
              vertical
              buttons={options}
              className="query-operator-editor"
            />
          ))}

          <ChecRow>
            <ChecCol className="editor-column" span={24}>
              {timers.map((timer) => (
                <TimerInput
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...timer}
                  key={timer.label}
                  id={timer.label}
                  hint={strings.timer_initial_value_hint}
                  mask={TIMER_MASK_HMMSSS}
                  className="form-field"
                />
              ))}
            </ChecCol>
          </ChecRow>
        </>
      ) : (
        <>
          <DrawerDivider />
          {operators.map((operator) => (
            <ReadOnlyParameterValue
              key={operator.label}
              label={operator.label}
              value={operator.currentValue.toUpperCase()}
            />
          ))}
          <DrawerDivider />
          {timers.map((timer) => (
            <ReadOnlyParameterValue
              key={timer.label}
              label={timer.label}
              value={timerValue(timer.value)}
            />
          ))}
        </>
      )}

      {/* PBI 266671 - Learn How This Logic Set Works */}
      {conditions.length > 0 && (
        <LearnHowLink
          onClick={() => setShowInfoModal(true)}
          hideBorder={logicSetEditable}
        >
          <InfoIcon
            width={16}
            height={16}
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
          />
          {strings.learn_how_logic_set_works}
        </LearnHowLink>
      )}

      <OutputtingDataTo uri={uri} />

      {/* PBI 266671 - Logic Set Info Modal */}
      <LogicSetInfoModal
        visible={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        logicSetName={description}
        conditions={conditions}
        abComplexQueryOperator={abComplexQueryOperator}
        cdComplexQueryOperator={cdComplexQueryOperator}
        finalComplexQueryOperator={finalComplexQueryOperator}
        delayActivate={delayActivate}
        delayDeactivate={delayDeactivate}
      />
    </Editor>
  )
}

LogicSetEditor.propTypes = {
  className: PropTypes.string,
  query: PropTypes.shape({
    conditions: PropTypes.array,
    markedForDeletion: PropTypes.bool,
    description: PropTypes.string,
  }),
  uri: PropTypes.string,
  description: PropTypes.string,
  finalComplexQueryOperator: PropTypes.string,
  abComplexQueryOperator: PropTypes.string,
  updateFinalOperator: PropTypes.func,
  cdComplexQueryOperator: PropTypes.string,
  updateABOperator: PropTypes.func,
  updateCDOperator: PropTypes.func,
  updateFeatureDelayTimerActivate: PropTypes.func,
  updateFeatureDelayTimerDeactivate: PropTypes.func,
  delayActivate: PropTypes.number,
  delayDeactivate: PropTypes.number,
  updateDescription: PropTypes.func,
  onEditorChange: PropTypes.func,
  toggleMarkForDeletion: PropTypes.func,
  deleteLogicSet: PropTypes.func,
  toggleMask: PropTypes.func,
  closeDrawer: PropTypes.func,
  resetLogicSet: PropTypes.func,
  backToFunctionEditor: PropTypes.func,
  disableDrawerHandler: PropTypes.func,
  featureInDesign: PropTypes.bool,
  isEditing: PropTypes.bool,
  isSimulating: PropTypes.bool,
  allDescriptions: PropTypes.arrayOf(PropTypes.string),
  isOrphan: PropTypes.bool,
}

export default compose(withStyle)(LogicSetEditor)
